import React, { useState, useEffect } from "react";
import { collaborationApi } from "../../services/api/collaborationApi";
import { toast } from "react-toastify";
import FeedSidebar from "./FeedSidebar";
import FeedPost from "./FeedPost";
import PostSkeleton from "./PostSkeleton";
import { FaSearch, FaTimes } from "react-icons/fa";
import "./BrowseOpportunities.css";

const BrowseOpportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skillsFilter, setSkillsFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const LIMIT = 20;

  useEffect(() => {
    loadOpportunities(true);
  }, []);

  const loadOpportunities = async (reset = false) => {
    const isInitialLoad = reset || offset === 0;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const skillsArray = skillsFilter
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const result = await collaborationApi.getOpportunities({
        limit: LIMIT,
        offset: reset ? 0 : offset,
        skills: skillsArray,
      });

      if (result.data.success) {
        const newOpportunities = result.data.opportunities || [];

        if (reset) {
          setOpportunities(newOpportunities);
          setOffset(LIMIT);
        } else {
          setOpportunities([...opportunities, ...newOpportunities]);
          setOffset(offset + LIMIT);
        }

        setHasMore(newOpportunities.length === LIMIT);
      }
    } catch (error) {
      toast.error("Failed to load opportunities");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    setOffset(0);
    loadOpportunities(true);
  };

  const handleClearFilter = () => {
    setSkillsFilter("");
    setOffset(0);
    loadOpportunities(true);
  };

  const handleLoadMore = () => {
    loadOpportunities(false);
  };

  const handlePostUpdate = (postId, updates) => {
    setOpportunities((prev) =>
      prev.map((post) => (post._id === postId ? { ...post, ...updates } : post))
    );
  };

  const handlePostDelete = (postId) => {
    setOpportunities((prev) => prev.filter((post) => post._id !== postId));
  };

  return (
    <div className="browse-opportunities-page">
      <div className="bop-layout">
      <div className="browse-opportunities-main">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Browse Collaboration Opportunities</h1>
          <p className="header-subtitle">
            Discover exciting research collaborations and join innovative
            projects
          </p>
        </div>
      </div>

      {/* Search/Filter Section */}
      <div className="search-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Filter by skills (e.g., Python, Machine Learning)"
              value={skillsFilter}
              onChange={(e) => setSkillsFilter(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
            {skillsFilter && (
              <button
                className="clear-filter-btn"
                onClick={handleClearFilter}
                title="Clear filter"
              >
                <FaTimes />
              </button>
            )}
          </div>
          <button className="search-btn" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      {/* Opportunities Feed */}
      <div className="opportunities-container">
        {loading ? (
          // Loading skeletons
          <>
            <PostSkeleton />
            <PostSkeleton />
            <PostSkeleton />
          </>
        ) : opportunities.length === 0 ? (
          // Empty state
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No opportunities found</h3>
            <p>
              {skillsFilter
                ? "Try adjusting your search filters"
                : "There are no collaboration opportunities at the moment"}
            </p>
          </div>
        ) : (
          // Opportunities list
          <>
            <div className="opportunities-list">
              {opportunities.map((post) => (
                <FeedPost
                  key={post._id}
                  post={post}
                  onUpdate={handlePostUpdate}
                  onDelete={handlePostDelete}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="load-more-section">
                <button
                  className="load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading..." : "Load More Opportunities"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      </div>

      <FeedSidebar />
      </div>
    </div>
  );
};

export default BrowseOpportunities;
